import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Menu, Phone } from 'lucide-react';
import { useQuoteModal } from '@/context/QuoteModalContext';
import { hasEnPosts } from '@/generated/blog/flags';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { openModal } = useQuoteModal();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mainNav = [
    { label: 'Services', href: '/services' },
    { label: 'About', href: '/about-us' },
    // Blog link hidden until at least one English post exists.
    ...(hasEnPosts ? [{ label: 'Blog', href: '/blog' }] : []),
    { label: 'Contact', href: '/contact-us' }
  ];

  const isActive = (href) => {
    return location.pathname === href;
  };

  const handleQuoteClick = () => {
    setMobileOpen(false);
    openModal();
  };

  return (
    <header className={`sticky top-0 z-50 w-full border-b border-neutral-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70 transition-shadow duration-150 ${scrolled ? 'shadow-sm' : ''}`} data-testid="header">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1180px]">
        <div className="flex h-20 items-center justify-between">
          {/* Logo - Horizontal wordmark, height-based sizing, no width constraints */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_jfi-redesign-1/artifacts/jgq5um9c_JFI-logo-large-webonly%2003jan2026.png" 
              alt="John Fleming Insurance - Burnaby BC Insurance Broker"
              className="h-[48px] sm:h-[56px] lg:h-[64px] w-auto"
              style={{ maxWidth: 'none' }}
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-5">
            {mainNav.map((item) => (
              <Link 
                key={item.label} 
                to={item.href} 
                className={`text-sm font-medium transition-all duration-150 ease-out ${
                  isActive(item.href) 
                    ? 'text-[#111111] underline decoration-cta underline-offset-4' 
                    : 'text-neutral-700 hover:text-[#1F1F1F]'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden md:flex items-center space-x-3">
            <span className="text-sm text-neutral-600 font-medium">
              604-298-1218
            </span>
            <Button 
              size="sm" 
              className="bg-cta hover:bg-cta-hover hover:shadow-md transition-all duration-200 ease-out" 
              data-testid="header-quote-button"
              onClick={handleQuoteClick}
            >
              Get a Quote
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="sm" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col space-y-4 mt-8">
                {mainNav.map((item) => (
                  <Link 
                    key={item.label} 
                    to={item.href} 
                    onClick={() => setMobileOpen(false)} 
                    className={`text-base font-medium transition-all duration-150 ease-out ${
                      isActive(item.href) 
                        ? 'text-cta' 
                        : 'text-neutral-700'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="pt-4 border-t space-y-3">
                  <p className="text-sm text-neutral-600 text-center">604-298-1218</p>
                  <Button 
                    variant="outline" 
                    className="w-full border border-neutral-300 text-cta hover:border-neutral-400 hover:bg-neutral-100 transition-all duration-200 ease-out" 
                    asChild
                  >
                    <a href="tel:604-298-1218">
                      <Phone className="h-4 w-4 mr-2" />
                      Call Us
                    </a>
                  </Button>
                  <Button 
                    className="w-full bg-cta hover:bg-cta-hover hover:shadow-md transition-all duration-200 ease-out"
                    onClick={handleQuoteClick}
                  >
                    Get a Quote
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
